<template>
    <div
        class="convo"
        @mousemove.once="handleMouseMove"
        @keydown.once="handleKeyDown"
    >
        <img v-if="trackingUrl" :src="trackingUrl" alt="" />
        <header v-if="!isInAppSurvey">
            <img id="company-logo" :src="getCDNUrl(logoSrc)" alt="" />
        </header>
        <div class="survey-container">
            <TransitionGroup name="list">
                <ConversationDisplayItems
                    v-for="(surveyNode, idx) in surveyDisplayNodes"
                    :key="idx"
                    :survey-node="surveyNode"
                    :survey-display-values="surveyDisplayValues"
                    :survey-params="props.params"
                    :survey-questions-and-answers="surveyQuestionsAndAnswers"
                    :is-active="isNodeActive(idx)"
                    :is-followed-by-input="isNextNodeTheLastInput(idx)"
                    :survey-spec="surveySpec"
                    :csat-labels="props.csatLabels"
                    :is-preview="isInPreviewMode"
                    :option-labels="getOptionLabels(idx)"
                    @nextNode="handleNext"
                    @updateScore="handleUpdateScore"
                    @saveAiQuestion="saveAiQuestion"
                />
            </TransitionGroup>
        </div>
        <transition name="fade">
            <div
                v-if="showFinishedReloadWarning && props.surveyLinkConfig.slug"
            >
                <div class="message app-message" @click="returnToSurveyLink">
                    <div class="bubble">Click here to reset survey</div>
                </div>
            </div>
        </transition>
        <Footer
            :nobranding="props.params.nobranding"
            :referrer-url="props.params.referrerUrl"
        />
    </div>
</template>

<script lang="ts" setup>
/**
 * This survey can run in two contexts:
 *
 * From email link:
 *    - answer and rating are known at mount time
 *    - initial score is automatically sent to the server on load
 *    - any subsequent question that tries to submit the initial score will be ignored (not displayed)
 *
 * From inapp:
 *    - answer is unknown at load time
 *    - response to lead question sends answer to server
 *    - rating is derived from answer received clientside
 */

import Footer from './components/visualisation/Footer.vue'
import ConversationDisplayItems from './components/visualisation/ConversationDisplayItems.vue'
import { answerToNPS, getQuestionAnswerLabel } from '@/domain/NPS'

import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue'
import type { Ref } from 'vue'
import {
    Block,
    ConversationSpec,
    ConversationItem,
    CurrentAnswer,
    Cursor,
    Options,
    SurveyDisplayValues,
    SurveyParams,
    QUESTION_KEY_AI,
    TYPE_REVIEW_LINK_BUTTON,
} from '@/entities/conversation'
import { sleep } from '@/utils/async'
import { answerToFiveScore } from '@/domain/NPS'
import { init as initConfetti } from '@/animations/confetti'
import qs from 'qs'
import { getCDNUrl } from '@/utils/cdn'
import { defaultCsatOptions } from '@/transforms/conversation'
import { SurveyLinkConfig } from '@/entities/appstore/survey-links'
import {
    finished,
    stillHere,
    preloadYearInReviewFinishedPage,
    sendReplyDiscreetlyWithRetryOnNetworkFail,
} from '@/api/conversation'

declare global {
    interface Window {
        askNicelyInterface: {
            postMessage: (message: string) => void
        }
        ReactNativeWebView: {
            postMessage: (message: string) => void
        }
        webkit: {
            messageHandlers: {
                askNicelyInterface: {
                    postMessage: (message: string) => void
                }
                ReactNativeWebView: {
                    postMessage: (message: string) => void
                }
            }
        }
    }
}

interface Props {
    preview?: boolean
    params: SurveyParams
    spec: ConversationSpec
    browserTracking: boolean
    surveyLinkConfig: SurveyLinkConfig
    csatLabels: Options
}

const props = withDefaults(defineProps<Props>(), {
    csatLabels: () => defaultCsatOptions(),
})

const surveySpec = ref()
const surveyParams = ref()
const surveyCurrentNode: Ref<ConversationItem | Block | null> = ref(null)
const surveyDisplayNodes: Ref<ConversationItem[]> = ref([])
const surveyDisplayValues: Ref<SurveyDisplayValues> = ref({})
const surveySpecCursor: Ref<Cursor> = ref([0, 'items', 0])
const surveyLastSaidText = ref('')
const surveyInitialScore = ref()
const surveyQuestionsAndAnswers: Ref<CurrentAnswer[]> = ref([])
const isInAppSurvey = ref(false)
const isInPreviewMode = ref(false)
const logoSrc = ref('')
const refreshSurveyTimer: Ref<NodeJS.Timeout | null> = ref(null)
const showFinishedReloadWarning = ref(false)
const surveyScore = ref()
const isSurveyFinished = ref(false)

const trackingUrl = ref('')
const hasMouse = ref(false)
const hasKeyBoard = ref(false)

const computedRating = computed(() => {
    const answer = Number(surveyScore.value)
    return props.params.isFiveScore
        ? answerToFiveScore(answer)
        : answerToNPS(answer)
})

const STILL_HERE_SECONDS = 60
const AUTO_REFRESH_SECONDS = 10
const STORAGE_KEY = 'asknicely-conversation'
const RESET_EVENTS = [
    'click',
    'keydown',
    'mousedown',
    'mousemove',
    'scroll',
    'touchstart',
    'touchmove',
]

const storageKey = computed(() => {
    const slug = isInPreviewMode.value ? 'preview' : props.params.slug

    return `${STORAGE_KEY}/${slug}/${props.params.template}/${props.params.version}`
})

onBeforeMount(() => {
    surveySpec.value = props.spec
    surveyParams.value = props.params
    isInAppSurvey.value = !!props.params.inapp
    isInPreviewMode.value = props?.preview
    logoSrc.value = props.params.logo
    surveyScore.value = /^\d+$/.test(props.params.answer)
        ? parseInt(props.params.answer, 10)
        : null

    if (isInPreviewMode.value) {
        resetLocalData()
    }

    loadLocalData()

    if (
        surveyInitialScore.value !== undefined &&
        surveyInitialScore.value !== surveyParams.value.answer
    ) {
        resetSurvey()
    }
})

onMounted(() => {
    RESET_EVENTS.forEach((rev) => document.addEventListener(rev, resetTimeout))

    if (isInAppSurvey.value) {
        postToParent({ key: 'conversation_loaded' })
    }

    if (!isInPreviewMode.value) {
        startStillHereInterval()
        // Add this condition to preload the Year In Review page
        if (props.params.has_christmas === '1' && !isSurveyFinished.value) {
            preloadYearInReviewFinishedPage(props.params.slug)
        }
    }

    if (
        surveyInitialScore.value === undefined &&
        !props.params.inapp &&
        !isInPreviewMode.value &&
        !isSurveyFinished.value
    ) {
        surveyInitialScore.value = props.params.answer
        postToParent({
            key: 'initial_score',
        })

        sendReplyDiscreetlyWithRetryOnNetworkFail(props.params.slug, {
            key: 'initial_score',
            value: props.params.answer,
            inputType: 'ReceiveText',
            questionText: '',
            template: props.params.template,
            version: Number(props.params.version),
            answer: Number(props.params.answer),
            conversationSlug: props.params.conversationSlug,
        })
    }

    if (
        surveyCurrentNode.value?.type === 'ReceiveText' ||
        surveyCurrentNode.value?.type === 'ReceiveOption'
    ) {
        return
    }
    moveToNextSurveyNode()
})

onBeforeUnmount(() => {
    RESET_EVENTS.forEach((rev) =>
        document.removeEventListener(rev, resetTimeout)
    )
})

const getOptionLabels = (idx) => {
    const surveyNode = surveyDisplayNodes.value.at(idx)
    const previousSurveyNode = surveyDisplayNodes.value.at(idx - 1)

    const isReceiveOptionNode =
        surveyNode.type === 'ReceiveOption' && !surveyNode.link

    if (isReceiveOptionNode && previousSurveyNode?.optionLabels) {
        return previousSurveyNode.optionLabels
    }

    return null
}

const loadLocalData = () => {
    const savedData = localStorage.getItem(storageKey.value)

    if (savedData) {
        const parsedData = JSON.parse(savedData)
        surveyCurrentNode.value = parsedData?.currentNode
        surveyDisplayNodes.value = parsedData?.displayNodes
        surveySpecCursor.value = parsedData?.specCursor
        surveyDisplayValues.value = parsedData?.values
        surveyLastSaidText.value = parsedData?.lastSaidText
        surveyInitialScore.value = parsedData?.initialScore
        surveyQuestionsAndAnswers.value = parsedData?.questionsAndAnswers ?? []
        isSurveyFinished.value = parsedData?.finished
    }
}

const resetLocalData = () => {
    localStorage.removeItem(storageKey.value)
}

const saveLocalData = () => {
    const data = {
        currentNode: surveyCurrentNode.value,
        displayNodes: surveyDisplayNodes.value,
        specCursor: surveySpecCursor.value,
        values: surveyDisplayValues.value,
        lastSaidText: surveyLastSaidText.value,
        initialScore: surveyInitialScore.value,
        questionsAndAnswers: surveyQuestionsAndAnswers.value,
        finished: isSurveyFinished.value,
    }
    localStorage.setItem(storageKey.value, JSON.stringify(data))
}

const resetSurvey = () => {
    surveyCurrentNode.value = null
    surveyInitialScore.value = props.params.answer
    surveyDisplayNodes.value = []
    surveyDisplayValues.value = {}
    surveySpecCursor.value = [0, 'items', 0]
    surveyLastSaidText.value = ''
    isSurveyFinished.value = false
}

const handleNext = (newValue, answerString = null) => {
    if (newValue) {
        surveyDisplayValues.value = {
            ...surveyDisplayValues.value,
            ...newValue,
        }

        if (newValue['answer'] && newValue['answer'] !== '') {
            surveyScore.value = newValue['answer']
        }

        const hasLastAnswer = newValue[surveyCurrentNode?.value?.param]
        if (hasLastAnswer && surveyLastSaidText.value && answerString) {
            surveyQuestionsAndAnswers.value.push({
                question: surveyLastSaidText.value,
                answer: answerString,
            })
        }
    }

    if (isInPreviewMode.value) {
        moveToNextSurveyNode()
        return
    }

    const lastSurveyDisplayNode = surveyDisplayNodes.value.at(-1)
    if (
        lastSurveyDisplayNode &&
        ['ReceiveText', 'ReceiveOption'].includes(lastSurveyDisplayNode.type) &&
        lastSurveyDisplayNode.param !== TYPE_REVIEW_LINK_BUTTON &&
        !isInPreviewMode.value
    ) {
        const currentDisplayItemKey = lastSurveyDisplayNode.param
        const currentDisplayItemValues =
            surveyDisplayValues.value[currentDisplayItemKey] || ''
        const currentDisplayItemBadgeId = lastSurveyDisplayNode?.options?.find(
            (option) => option.key === currentDisplayItemKey
        )?.badgeId
        const hasMultipleValues =
            lastSurveyDisplayNode?.multiSelect &&
            currentDisplayItemValues.indexOf('|') > 1

        sendReplyDiscreetlyWithRetryOnNetworkFail(props.params.slug, {
            key: currentDisplayItemKey,
            value: currentDisplayItemValues,
            badgeId: currentDisplayItemBadgeId,
            inputType: lastSurveyDisplayNode.type,
            questionText: surveyLastSaidText.value,
            template: props.params.template,
            version: Number(props.params.version),
            answer: Number(props.params.answer),
            multipleValues: hasMultipleValues,
            conversationSlug: props.params.conversationSlug,
        })
    }

    moveToNextSurveyNode()
}

const saveAiQuestion = (aiQuestion, aiAnswer = null, aiQuestionIndex = 1) => {
    if (isInPreviewMode.value) {
        return
    }

    sendReplyDiscreetlyWithRetryOnNetworkFail(props.params.slug, {
        key: `ai_question_${aiQuestionIndex}`,
        value: aiAnswer,
        inputType: 'ReceiveText',
        questionText: aiQuestion,
        template: props.params.template,
        version: Number(props.params.version),
        answer: Number(props.params.answer),
        multipleValues: false,
        conversationSlug: props.params.conversationSlug,
    })
}

const moveToNextSurveyNode = () => {
    let nextSurveyDisplayNode: ConversationItem | Block | null =
        getNextSurveyDisplayNode(surveySpecCursor.value, surveySpec.value)

    const currentNode = surveyCurrentNode.value

    surveyCurrentNode.value = nextSurveyDisplayNode
    saveLocalData()

    if (isSurveyFinished.value) {
        return
    }

    if (!nextSurveyDisplayNode) {
        triggerSurveyFinishedActions()
        return
    }

    if (['Block', 'Jump', 'For'].includes(nextSurveyDisplayNode.type)) {
        // Don't display 'BLOCK', 'JUMP' or 'FOR' nodes on the screen
        const nextCursor = getNextSurveySpecCursor(nextSurveyDisplayNode)
        surveySpecCursor.value = nextCursor
        moveToNextSurveyNode()
        return
    }

    if (
        currentNode?.type === 'Say' &&
        currentNode?.aiConversationalMode &&
        nextSurveyDisplayNode.type === 'ReceiveText' &&
        nextSurveyDisplayNode.param === QUESTION_KEY_AI
    ) {
        // Don't display 'ReceiveText' of an AI question (that's part of the 'Say')
        const nextCursor = getNextSurveySpecCursor(nextSurveyDisplayNode)
        surveySpecCursor.value = nextCursor
        moveToNextSurveyNode()
        return
    }

    if (nextSurveyDisplayNode.type === 'Say') {
        surveyLastSaidText.value = nextSurveyDisplayNode.value
    }

    surveyDisplayNodes.value.push(nextSurveyDisplayNode)
    const nextCursor = getNextSurveySpecCursor(nextSurveyDisplayNode)
    surveySpecCursor.value = nextCursor

    scrollToLastElement()
}

const getNextSurveyDisplayNode = (
    cursor,
    spec
): ConversationItem | Block | null => {
    if (!cursor || cursor.length === 0) {
        return null
    }

    const displayNode = cursor?.reduce(
        (currentPath, currentNode) => currentPath[currentNode],
        spec
    )

    if (displayNode?.type === 'Block' && displayNode?.preventFallthrough) {
        return null
    }
    return displayNode
}

const getNextSurveySpecCursor = (lastSurveyDisplayNode): Cursor => {
    switch (lastSurveyDisplayNode.type) {
        case 'For': {
            if (lastSurveyDisplayNode.match === 'rating') {
                const answer = Number(
                    surveyParams.value.answer ?? surveyScore.value
                )
                let answerType: string = answerToNPS(answer)
                let fiveScoreAsNPSAnswerType = ''
                if (surveyParams.value.isFiveScore) {
                    const questionType =
                        surveyParams.value.questionType === 'fivestar'
                            ? '5star'
                            : surveyParams.value.questionType
                    answerType = getQuestionAnswerLabel(questionType, answer)

                    // For existing surveys that already use NPS language for csat/fivestar
                    fiveScoreAsNPSAnswerType = answerToFiveScore(answer)
                }

                if (lastSurveyDisplayNode.cases[answerType]?.length > 0) {
                    return [...surveySpecCursor.value, 'cases', answerType, 0]
                } else if (
                    lastSurveyDisplayNode.cases[fiveScoreAsNPSAnswerType]
                        ?.length > 0
                ) {
                    return [
                        ...surveySpecCursor.value,
                        'cases',
                        fiveScoreAsNPSAnswerType,
                        0,
                    ]
                } else if (lastSurveyDisplayNode.cases['default']?.length > 0) {
                    return [...surveySpecCursor.value, 'cases', 'default', 0]
                }
            } else {
                const caseAnswer =
                    surveyDisplayValues.value[lastSurveyDisplayNode.match]

                if (lastSurveyDisplayNode.cases[caseAnswer]?.length > 0) {
                    return [...surveySpecCursor.value, 'cases', caseAnswer, 0]
                } else if (lastSurveyDisplayNode.cases['default']?.length > 0) {
                    return [...surveySpecCursor.value, 'cases', 'default', 0]
                }
            }
            break
        }

        case 'Jump':
        case 'Block': {
            for (let i = 0; i < surveySpec.value.length; i++) {
                const item = surveySpec.value[i]
                if (
                    item.type === 'Block' &&
                    item.label === lastSurveyDisplayNode.label
                ) {
                    return [i, 'items', 0] // reset to top level
                }
            }
            break
        }

        default: {
            break
        }
    }

    for (let i = surveySpecCursor.value.length - 1; i >= 0; i--) {
        // start at the end and go <-

        const key = surveySpecCursor.value[i]

        if (typeof key === 'number') {
            const nextCursor = surveySpecCursor.value
                .slice(0, i)
                .concat([key + 1])
            const nextSurveyDisplayNode = getNextSurveyDisplayNode(
                nextCursor,
                surveySpec.value
            )

            if (nextSurveyDisplayNode) {
                return nextCursor
            }
        }
    }

    return []
}

const scrollToLastElement = () => {
    // Find last element and wait transition before scrolling it into view
    const lastElement = document.querySelector(
        '.survey-display-item:last-child'
    )
    if (lastElement) {
        setTimeout(() => {
            lastElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }, 50)
    }
}

const isNodeActive = (idx) => {
    if (isSurveyFinished.value) {
        return false
    }

    const currentNode = surveyDisplayNodes.value[idx]
    const lastNode = surveyDisplayNodes.value.at(-1)

    return JSON.stringify(currentNode) === JSON.stringify(lastNode)
}

const isNextNodeTheLastInput = (idx) => {
    const nextNode = surveyDisplayNodes.value[idx + 1]
    const lastNode = surveyDisplayNodes.value.at(-1)

    if (!nextNode) {
        return false
    }

    return (
        JSON.stringify(nextNode) === JSON.stringify(lastNode) &&
        nextNode?.type.includes('Receive')
    )
}

const handleUpdateScore = async (data) => {
    sendReplyDiscreetlyWithRetryOnNetworkFail(props.params.slug, {
        key: 'update_score',
        value: data.score,
        inputType: 'ReceiveText',
        questionText: '',
        template: props.params.template,
        version: Number(props.params.version),
        answer: Number(props.params.answer),
        conversationSlug: props.params.conversationSlug,
    })
}

const resetTimeout = () => {
    if (props.surveyLinkConfig.autoRefresh && props.surveyLinkConfig.slug) {
        const interval = props.surveyLinkConfig.timeout ?? STILL_HERE_SECONDS
        if (refreshSurveyTimer.value) {
            clearTimeout(refreshSurveyTimer.value)
        }
        refreshSurveyTimer.value = setTimeout(() => {
            returnToSurveyLink()
        }, interval * 1000)
    }
}

const handleMouseMove = () => {
    hasMouse.value = true
    addTrackingImage()
}

const handleKeyDown = () => {
    hasKeyBoard.value = true
    addTrackingImage()
}

const addTrackingImage = () => {
    if (!props.browserTracking) {
        return false
    }
    const resolution = window.screen.width + '*' + window.screen.height

    const params: { [key: string]: any } = {}
    params.res = resolution
    if (hasMouse.value) {
        params.mouse = 1
    }
    if (hasKeyBoard.value) {
        params.keyboard = 1
    }

    trackingUrl.value = `/email/trackbrowser/${props.params.slug}?${qs.stringify(
        params
    )}`
}

const setupAutoRefresh = () => {
    if (props.surveyLinkConfig.autoRefresh && props.surveyLinkConfig.slug) {
        showFinishedReloadWarning.value = true
        setTimeout(() => {
            returnToSurveyLink()
        }, AUTO_REFRESH_SECONDS * 1000)
    }
}

const returnToSurveyLink = () => {
    window.location.href = '/s/feedback/' + props.surveyLinkConfig.slug
}

const postToOS = (event, msg) => {
    let data = JSON.stringify({ event: event, msg: msg })
    if (window.webkit != undefined) {
        if (window.webkit.messageHandlers.askNicelyInterface != undefined) {
            window.webkit.messageHandlers.askNicelyInterface.postMessage(data)
        }
        if (window.webkit.messageHandlers.ReactNativeWebView != undefined) {
            window.webkit.messageHandlers.ReactNativeWebView.postMessage(data)
        }
    }
    if (window.askNicelyInterface != undefined) {
        window.askNicelyInterface.postMessage(data)
    }
    if (window.ReactNativeWebView != undefined) {
        window.ReactNativeWebView.postMessage(data)
    }
}

const postToParent = (
    data: { key: string; value?: any; showConfetti?: boolean },
    targetOrigin = '*'
) => {
    window.parent.postMessage(data, targetOrigin)
}

const startStillHereInterval = () => {
    const stillHereInterval = setInterval(() => {
        if (!isSurveyFinished.value) {
            stillHere(props.params.slug, STILL_HERE_SECONDS)
        } else {
            clearInterval(stillHereInterval)
        }
    }, STILL_HERE_SECONDS * 1000)
}

const throwConfetti = async () => {
    const W = window.outerWidth
    const H = window.outerHeight

    const canvas = document.createElement('canvas')
    document.body.appendChild(canvas)
    canvas.style.top = '100px'
    canvas.style.pointerEvents = 'none' // confentti should be non blocking and click through
    await initConfetti(30, canvas)
    document.body.removeChild(canvas)
}

const triggerSurveyFinishedActions = async () => {
    isSurveyFinished.value = true
    const showConfetti =
        !props.params.disableConfetti && computedRating.value === 'promoter'

    if (showConfetti) {
        await throwConfetti()
    } else {
        await sleep(600)
    }

    if (!isInPreviewMode.value) {
        await finished(props.params.slug, props.params.conversationSlug)
        postToOS('askNicelyDone', 'Success')
        postToParent({
            key: 'finished',
            value: surveyLastSaidText.value,
            showConfetti,
        })
    }

    // Christmas redirect
    if (props.params.has_christmas === '1') {
        window.location.href =
            '/email/conversation/' +
            props.params.slug +
            '/finished_year_in_review'
    }

    saveLocalData()
    setupAutoRefresh()
}
</script>

<style lang="less">
header {
    display: block;
    text-align: center;
    padding: 10px;
    transition: 0.3s padding;
    transition-timing-function: ease-in-out;
    img {
        max-width: 250px;
        display: inline-block;
    }
    &.splash {
        padding: 200px 10px 10px 10px;
        transition: 0.5s padding;
        transition-timing-function: ease-in-out;
    }
}

.survey-container {
    max-width: 600px;
    margin: 0px auto;
    padding: 20px 5px 110px 5px;
    position: relative;
}

.list-enter-active,
.list-leave-active {
    transition: all 0.3s ease-out;
}
.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateY(20px);
}
</style>
